import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Avatar, IconButton, Tooltip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import {
  Notifications as NotifIcon, Send, Refresh, People, Person,
  Business, CheckCircle, ErrorOutline,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';

const TYPE_OPTS = ['general', 'Attendance', 'Leave', 'Salary', 'Holiday'];

const TYPE_STYLE = {
  general:    { bg: '#e8eaf6', color: '#1a237e' },
  Attendance: { bg: '#e8f5e9', color: '#2e7d32' },
  Leave:      { bg: '#fff3e0', color: '#e65100' },
  Salary:     { bg: '#e0f2f1', color: '#00695c' },
  Holiday:    { bg: '#fce4ec', color: '#880e4f' },
};

const SendPanel = ({ departments, onSent }) => {
  const [mode, setMode] = useState('all'); 
  const [form, setForm] = useState({ title: '', message: '', type: 'general', department: '', student_id: '' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSend = async () => {
    if (!form.title || !form.message) { alert('Title and message are required'); return; }
    if (mode === 'dept' && !form.department) { alert('Please select a department'); return; }
    if (mode === 'single' && !form.student_id) { alert('Please enter Employee ID'); return; }

    setSending(true);
    setResult(null);

    let url, payload;
    if (mode === 'all') {
      url = Config.main_url + apiList.sendBulkNotificationsAll;
      payload = { title: form.title, message: form.message, type: form.type };
    } else if (mode === 'dept') {
      url = Config.main_url + '/sendBulkNotificationsByDept';
      payload = { title: form.title, message: form.message, type: form.type, department: form.department };
    } else {
      url = Config.main_url + apiList.sendEmployeeNotification;
      payload = { title: form.title, message: form.message, type: form.type, student_id: form.student_id };
    }

    const data = await InvokeApi(url, payload);
    setSending(false);

    if (data?.code === '200') {
      setResult({ success: true, message: data.message || 'Notification sent successfully' });
      setForm({ title: '', message: '', type: 'general', department: '', student_id: '' });
      onSent?.();
    } else {
      setResult({ success: false, message: data?.message || 'Failed to send notification' });
    }
  };

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Send sx={{ color: '#1a237e' }} />
          <Typography variant="h6" fontWeight={800} color="#1a237e">Send Notification</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          {[
            { key: 'all',    label: 'All Employees', icon: <People fontSize="small" /> },
            { key: 'dept',   label: 'By Department', icon: <Business fontSize="small" /> },
            { key: 'single', label: 'Single Employee', icon: <Person fontSize="small" /> },
          ].map(m => (
            <Box key={m.key} onClick={() => setMode(m.key)} sx={{
              display: 'flex', alignItems: 'center', gap: 0.8, px: 2, py: 1,
              borderRadius: 2, cursor: 'pointer', border: '1.5px solid',
              borderColor: mode === m.key ? '#1a237e' : '#e8eaf6',
              background: mode === m.key ? '#e8eaf6' : '#fff',
              transition: 'all 0.2s',
            }}>
              <Box sx={{ color: mode === m.key ? '#1a237e' : 'text.secondary' }}>{m.icon}</Box>
              <Typography fontSize={12} fontWeight={700} color={mode === m.key ? '#1a237e' : 'text.secondary'}>
                {m.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Grid container spacing={2}>
          {mode === 'dept' && (
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Department *" value={form.department} onChange={f('department')}>
                {departments.map(d => <MenuItem key={d.ID} value={d.department}>{d.department}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          {mode === 'single' && (
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Employee ID *" value={form.student_id} onChange={f('student_id')} />
            </Grid>
          )}

          <Grid item xs={12} sm={8}>
            <TextField fullWidth size="small" label="Title *" value={form.title} onChange={f('title')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Type" value={form.type} onChange={f('type')}>
              {TYPE_OPTS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Message *" value={form.message}
              onChange={f('message')} multiline rows={3} />
          </Grid>
        </Grid>

        {result && (
          <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2, borderRadius: 2 }}
            icon={result.success ? <CheckCircle /> : <ErrorOutline />}>
            {result.message}
          </Alert>
        )}

        <Button fullWidth variant="contained" startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
          onClick={handleSend} disabled={sending}
          sx={{ mt: 2.5, background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, py: 1.2 }}>
          {sending ? 'Sending...' : `Send to ${mode === 'all' ? 'All Employees' : mode === 'dept' ? form.department || 'Department' : `Employee #${form.student_id || '?'}`}`}
        </Button>
      </CardContent>
    </Card>
  );
};

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [filterDate, setFilterDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [empIdFilter, setEmpIdFilter] = useState('');
  const [historyDialog, setHistoryDialog] = useState(false);
  const [historyEmpId, setHistoryEmpId] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const loadDepts = async () => {
      const data = await InvokeApi(Config.main_url + apiList.getDeptartments, { status: 'Active' });
      setDepartments(data?.department || []);
    };
    loadDepts();
  }, []);

  const loadByDate = useCallback(async () => {
    setLoading(true);
    const d = dayjs(filterDate);
    const data = await InvokeApi(Config.main_url + apiList.getEmployeeNotificationsByDate, {
      year: d.year(), month: d.month() + 1, day: d.date(),
    });
    setNotifications(data?.notifications || []);
    setLoading(false);
  }, [filterDate]);

  useEffect(() => { loadByDate(); }, [loadByDate]);

  const loadHistory = async () => {
    if (!historyEmpId) { alert('Enter Employee ID'); return; }
    setHistoryLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getEmployeeNotifications, { student_id: historyEmpId });
    setHistoryData(data?.notifications || []);
    setHistoryLoading(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Notifications</Typography>
          <Typography variant="body2" color="text.secondary">Send and view employee notifications</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setHistoryDialog(true)}
            startIcon={<NotifIcon />}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e', fontWeight: 600 }}>
            Employee History
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadByDate} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <SendPanel departments={departments} onSent={loadByDate} />
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotifIcon sx={{ color: '#1a237e' }} />
                  <Typography variant="h6" fontWeight={800} color="#1a237e">Sent Notifications</Typography>
                </Box>
                <TextField type="date" size="small" value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  InputLabelProps={{ shrink: true }} label="Date" sx={{ minWidth: 160 }} />
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                  <CircularProgress sx={{ color: '#1a237e' }} />
                </Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <NotifIcon sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
                  <Typography color="text.secondary" fontWeight={500}>No notifications on {dayjs(filterDate).format('DD MMM YYYY')}</Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary" mb={1} display="block">
                    {notifications.length} notification{notifications.length !== 1 ? 's' : ''} sent
                  </Typography>
                  {notifications.map((n, i) => {
                    const style = TYPE_STYLE[n.type] || TYPE_STYLE.general;
                    return (
                      <Box key={n.ID || i}>
                        <Box sx={{ display: 'flex', gap: 2, py: 1.5, alignItems: 'flex-start' }}>
                          <Avatar sx={{ width: 38, height: 38, background: style.bg, flexShrink: 0 }}>
                            <NotifIcon sx={{ color: style.color, fontSize: 18 }} />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.3 }}>
                              <Typography fontSize={13} fontWeight={700}>{n.title}</Typography>
                              <Chip label={n.type || 'general'} size="small"
                                sx={{ background: style.bg, color: style.color, fontWeight: 600, fontSize: 10, height: 18 }} />
                              <Chip label={`Emp #${n.student_id}`} size="small"
                                sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 10, height: 18 }} />
                            </Box>
                            <Typography fontSize={12} color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                              {n.message}
                            </Typography>
                            <Typography fontSize={10} color="text.disabled" mt={0.3}>
                              {n.created_at ? dayjs(n.created_at).format('hh:mm A') : '—'}
                            </Typography>
                          </Box>
                        </Box>
                        {i < notifications.length - 1 && <Divider />}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={historyDialog} onClose={() => setHistoryDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotifIcon /> Employee Notification History
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField fullWidth size="small" label="Employee ID" value={historyEmpId}
              onChange={(e) => setHistoryEmpId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadHistory()} />
            <Button variant="contained" onClick={loadHistory} disabled={historyLoading}
              sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 80 }}>
              {historyLoading ? <CircularProgress size={18} color="inherit" /> : 'Search'}
            </Button>
          </Box>

          {historyData.length === 0 && !historyLoading ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              {historyEmpId ? 'No notifications found' : 'Enter an Employee ID to search'}
            </Typography>
          ) : (
            historyData.map((n, i) => {
              const style = TYPE_STYLE[n.type] || TYPE_STYLE.general;
              return (
                <Box key={n.ID || i}>
                  <Box sx={{ display: 'flex', gap: 1.5, py: 1.5 }}>
                    <Avatar sx={{ width: 34, height: 34, background: style.bg, flexShrink: 0 }}>
                      <NotifIcon sx={{ color: style.color, fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                        <Typography fontSize={13} fontWeight={700}>{n.title}</Typography>
                        <Chip label={n.type || 'general'} size="small"
                          sx={{ background: style.bg, color: style.color, fontWeight: 600, fontSize: 10, height: 18 }} />
                      </Box>
                      <Typography fontSize={12} color="text.secondary">{n.message}</Typography>
                      <Typography fontSize={10} color="text.disabled" mt={0.3}>
                        {n.created_at ? dayjs(n.created_at).format('DD MMM YYYY hh:mm A') : '—'}
                      </Typography>
                    </Box>
                  </Box>
                  {i < historyData.length - 1 && <Divider />}
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setHistoryDialog(false); setHistoryData([]); setHistoryEmpId(''); }}
            variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
